import React from 'react'
import {
    BadgeCheck,
    FileCheck2,
    GitBranch,
    Landmark,
    LayoutDashboard,
    Network,
    ShieldCheck,
    UsersRound,
} from 'lucide-react'

import serviceImage1 from '../../assets/1.png'
import serviceImage2 from '../../assets/2.png'
import serviceImage3 from '../../assets/3.png'
import serviceImage4 from '../../assets/4.png'
import serviceImage5 from '../../assets/5.png'
import serviceImage6 from '../../assets/6.png'


const serviceCards = [
    {
        title: 'Digital Lead Management',
        image: serviceImage1,

        description: 'Create, organize, allocate, track, and process customer loan inquiries from one centralized digital platform.',
        points: [
            'Digital lead creation',
            'Centralized lead database',
            'Real-time lead tracking',
            'Smart lead allocation',
        ],
    },
    {
        title: 'Customer Profiling & Documents',
        image: serviceImage2,

        description: 'Build complete customer profiles and manage identity, income, banking, and supporting documents securely.',
        points: [
            'Customer profile management',
            'Secure document storage',
            'Faster verification',
            'Easy record access',
        ],
    },
    {
        title: 'CIBIL & Eligibility Analysis',
        image: serviceImage3,

        description: 'Evaluate credit profile, income eligibility, liabilities, FOIR, risk parameters, and loan affordability.',
        points: [
            'CIBIL score analysis',
            'Income eligibility checks',
            'FOIR calculations',
            'Smarter loan assessment',
        ],
    },
    {
        title: 'Smart Bank Matching Engine',
        image: serviceImage4,

        description: 'Match customer profiles with suitable banks based on credit score, income structure, liabilities, and policy fit.',
        points: [
            'Policy-based bank matching',
            'Accurate bank selection',
            'Higher approval probability',
            'Reduced rejection rates',
        ],
    },
    {
        title: 'Real-Time Loan Tracking',
        image: serviceImage5,

        description: 'Give customers, DSAs, and teams instant visibility across lead progress, banker updates, queries, approvals, and rejections.',
        points: [
            'Live lead progress',
            'Query status tracking',
            'Approval stage updates',
            'Reduced follow-up dependency',
        ],
    },
    {
        title: 'Performance & Payout Management',
        image:serviceImage6,

        description: 'Track deals, performance, payouts, redeemable calculations, billing workflows, and revenue activity from one system.',
        points: [
            'Deal tracking',
            'Payout monitoring',
            'Team performance insights',
            'Revenue tracking',
        ],
    },
]

const platformModules = [
    {
        title: 'Customer Journey',
        description: 'A cleaner experience for customers from loan requirement capture to application tracking and real-time updates.',
        Icon: UsersRound,
    },
    {
        title: 'DSA Operations',
        description: 'A centralized workspace for lead handling, customer profiling, eligibility checks, documents, and follow-ups.',
        Icon: LayoutDashboard,
    },
    {
        title: 'Banker Coordination',
        description: 'Structured case information helps bankers review applications faster and take better-informed decisions.',
        Icon: Landmark,
    },
    {
        title: 'Workflow Automation',
        description: 'Lead movement, banker updates, status changes, notifications, and reporting stay synchronized across teams.',
        Icon: GitBranch,
    },
]


const workflowSteps = [
    'Create digital lead',
    'Capture customer profile and documents',
    'Analyze CIBIL and eligibility',
    'Match suitable banks automatically',
    'Route application to banker or branch',
    'Track queries, approvals, and rejections',
    'Manage payouts, billing, and reporting',
]

const outcomes = [
    'Complete loan workflow automation',
    'Intelligent bank recommendation engine',
    'Real-time synchronized operations',
    'Higher loan approval efficiency',
    'Reduced operational delays',
    'Better customer transparency',
    'Scalable fintech ecosystem',
    'Secure and structured workflow management',
]

const Services = () => {
    return (
        <>
            <header className="services-hero">
                <div className="container services-hero__inner px-4 sm:px-8 xl:px-4 reveal-on-scroll services-reveal-heading">

                    <p className="services-eyebrow">Our Services</p>
                   <h1>
    <span>Connected services for</span>
    <span className="services-hero__highlight">modern loan processing</span>
</h1>


                    <p>
                        We provide a technology-driven digital lending ecosystem that simplifies and automates the complete loan acquisition journey between customers, DSAs, bankers, branches, and financial institutions.

Our platform centralizes lead management, eligibility analysis, intelligent bank matching, real-time loan tracking, banker coordination, payout management, and operational workflows into one unified system — creating a faster, smarter, and more transparent lending experience for every stakeholder involved.
                    </p>
                </div>
            </header>

            <main className="services-page">
                <section className="services-section">
                    <div className="container services-section__inner px-4 sm:px-8 xl:px-4">
                        <div className="services-section__heading reveal-on-scroll services-reveal-heading">

                            <p className="services-eyebrow">Platform Capabilities</p>
                            <h2>Core services powering the loan processing system</h2>
                            <p>
                                These services eliminate manual dependency, improve operational efficiency, reduce
                                rejection rates, increase transparency, and create a seamless real-time lending workflow.
                            </p>
                        </div>

                        <div className="services-card-grid">
                            {serviceCards.map(({ title, image, description, points }, index) => (
    <article
        className="services-card reveal-on-scroll services-reveal-card"
        style={{ "--services-item-delay": `${index * 80}ms` }}
        key={title}
    >

                                    <div className="services-card__media">
                                        <img src={image} alt={title} />
                                    </div>

                                    <div className="services-card__body">
                                        <h3>{title}</h3>
                                        <p>{description}</p>

                                        <ul>
                                            {points.map((point) => (
                                                <li key={point}>
                                                    <BadgeCheck size={17} />
                                                    <span>{point}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="services-section services-section--accent">
                    <div className="container services-section__inner px-4 sm:px-8 xl:px-4">
                        <div className="services-section__heading">
                           <p className="services-eyebrow">Connected Users</p>
<h2>One software layer for every stakeholder</h2>
<p>
    The platform connects customers, DSAs, bankers, and operations teams so every case moves through one clear and synchronized workflow.
</p>

                        </div>

                        <div className="services-module-grid">
                            {platformModules.map(({ title, description, Icon }, index) => (
    <article
        className="services-module-card reveal-on-scroll services-reveal-card"
        style={{ "--services-item-delay": `${index * 85}ms` }}
        key={title}
    >

                                    <Icon size={24} />
                                    <h3>{title}</h3>
                                    <p>{description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="services-workflow">
                    <div className="container services-workflow__inner px-4 sm:px-8 xl:px-4">
                        <div className="reveal-on-scroll services-reveal-heading">
    <p className="services-eyebrow">Digital Workflow</p>

                            <h2>From inquiry to decision tracking</h2>
                            <p>
                                Every case moves through a structured digital path, from lead creation and eligibility
                                analysis to bank routing, banker action, customer updates, payout tracking, and reporting.
                            </p>
                        </div>

                        <div className="services-step-list">
                            {workflowSteps.map((step, index) => (
                                <div
    className="services-step reveal-on-scroll services-reveal-step"
    style={{ "--services-item-delay": `${index * 65}ms` }}
    key={step}
>

                                    <span>{String(index + 1).padStart(2, '0')}</span>
                                    <p>{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="services-section">
                    <div className="container services-section__inner px-4 sm:px-8 xl:px-4">
                        <div className="services-section__heading">
                            <p className="services-eyebrow">Why Choose Us</p>
                            <h2>Built to make lending operations faster, clearer, and more scalable</h2>
                        </div>

                        <div className="services-outcome-grid">
                            {outcomes.map((item, index) => (
    <div
        className="services-outcome reveal-on-scroll services-reveal-card"
        style={{ "--services-item-delay": `${index * 55}ms` }}
        key={item}
    >

                                    <ShieldCheck size={20} />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

               
            </main>
        </>
    )
}

export default Services
